import './bootstrap';

import { Apii } from './services/api.js';
import { impersonationManager } from './utils/impersonation.js';
import { createSpaPageviewTracker } from './utils/woopraPageview.js';
import Filters from './filters';

import { createApp, h } from 'vue';
//import { createSSRApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { axiosAdapter } from '@inertiajs/core';
import { createPinia } from 'pinia';
import Notifications from 'notiwind';
import tooltip from '@/directives/tooltip.js';
import VueCookies from 'vue-cookies';
import VueNumberFormat from 'vue-number-format';
import debounce from '@/directives/debounce';
import vClickOutside from 'click-outside-vue3';
import InputFormat from '@/directives/input-format';
import DecimalFormat from '@/directives/decimal-format';
import safeHtml from '@/directives/safe-html';
import SafeQrCode from '@/components/SafeQrCode.vue';
import * as Sentry from '@sentry/vue';
import { dropExpectedClientErrors, IGNORED_CLIENT_ERROR_MESSAGES } from '@/utils/sentryFilters';
import notify from './plugins/notify';
import AnalyticsPlugin from './plugins/analytics';
import { getAnalytics } from './services/analyticsTracking';
import { getItem, setItem } from './utils/localStorage';
import Layout from './Layouts/Main.vue';
import GuestLayout from './Layouts/Guest.vue';
import AuthLayout from './Layouts/Auth.vue';
import { useThemeStore } from '@/store/theme';

const pinia = createPinia();

const trackWoopraSpaPageview = createSpaPageviewTracker();

const dsn = import.meta.env.VITE_SENTRY_DSN;
const appEnv = import.meta.env.VITE_APP_ENV;

// Defensive patch for Inertia.js response handling
// Prevents TypeError when this.response.headers is undefined
if (typeof window !== 'undefined') {
    // Add error handling for potential response header issues
    window.addEventListener('error', (event) => {
        if (
            event.error &&
            event.error.message &&
            event.error.message.includes(
                "undefined is not an object (evaluating 'this.response.headers"
            )
        ) {
            console.warn(
                'Inertia.js response headers error caught and handled:',
                event.error
            );
            event.preventDefault(); // Prevent the error from propagating
            return false;
        }
    });
}

createInertiaApp({
    // Inertia v3 ships its own XHR client. Keep routing Inertia visits through
    // the shared axios instance so the interceptors in bootstrap.js (401 auto
    // logout, network/504 retry + notification) still apply to page visits.
    http: axiosAdapter(window.axios),
    resolve: (name) => {
        const pages = import.meta.glob('./Pages/**/*.vue', { eager: true });
        let page = pages[`./Pages/${name}.vue`];
        if (name.startsWith('Auth/')) {
            // Respect page-defined layout if present
            page.default.layout = page.default.layout || AuthLayout;
        }
        if (name.startsWith('Guest/')) {
            page.default.layout = page.default.layout || GuestLayout;
        }
        // `layout: null` keeps a page out of the app layout, e.g. pages for
        // logged-out visitors whose header has no user to show.
        if (page.default.layout === undefined) {
            page.default.layout = Layout;
        }
        return page;
    },
    setup({ el, App, props, plugin }) {
        const app = createApp({
            render: () => h(App, props),
        })
            .use(plugin)
            .use(pinia)
            .use(Notifications)
            .use(VueCookies)
            .use(notify)
            .use(AnalyticsPlugin, { debug: appEnv !== 'production' })
            .use(VueNumberFormat, {
                prefix: '',
                precision: 0,
                decimal: '.',
                thousand: ',',
            });

        app.directive('tooltip', tooltip);
        app.directive('debounce', (el, binding) => debounce(el, binding));
        app.directive('ClickOutside', vClickOutside.directive);
        app.directive('InputFormat', InputFormat);
        app.directive('DecimalFormat', DecimalFormat);
        app.directive('safe-html', safeHtml);
        app.component('SafeQrCode', SafeQrCode);

        app.config.globalProperties.$filters = Filters;

        pinia.use(({ store }) => {
            store.$cookies = app.config.globalProperties.$cookies;
            store.$notify = app.config.globalProperties.$notify;
        });

        Sentry.init({
            app,
            dsn: dsn,
            ignoreErrors: [
                'acsbJS',
                'Load failed',
                'runtime.sendMessage',
                'sendNativeMessage',
                'Java',
                'operation is insecure',
                'chrome-extension',
                'antifingerprint',
                'Request aborted',
                'invalid origin',
                ...IGNORED_CLIENT_ERROR_MESSAGES,
            ],
            integrations: [
                Sentry.vueIntegration({
                    tracingOptions: {
                        trackComponents: true,
                        timeout: 500,
                    },
                }),
                Sentry.thirdPartyErrorFilterIntegration({
                    filterKeys: ['my10xisthebest'],
                    // - 'drop-error-if-contains-third-party-frames'
                    // - 'drop-error-if-exclusively-contains-third-party-frames'
                    // - 'apply-tag-if-contains-third-party-frames'
                    // - 'apply-tag-if-exclusively-contains-third-party-frames'
                    behaviour: 'drop-error-if-contains-third-party-frames',
                }),
                Sentry.replayIntegration(),
            ],
            tracePropagationTargets: ['my.10xtravel.com'],
            allowUrls: [/https:\/\/my\.10xtravel\.com/],
            denyUrls: [/https?:\/\/my10x\.test/],
            // Session Replay
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            beforeSend: dropExpectedClientErrors,
            beforeSendTransaction(event) {
                return event;
            },
        });

        window.ApiService = new Apii();

        // Initialize impersonation manager
        impersonationManager
            .initialize()
            .then(() => {})
            .catch((error) => {
                //
            });

        app.mount(el);

        // NOTE: Inertia fires a `navigate` event on the initial hard load too,
        // and it does so in a microtask (deferred via page.set().then()), so it
        // is delivered AFTER this listener is registered below. The Woopra SPA
        // tracker relies on that ordering to skip the first event (the hard load
        // is already tracked by woopra-trk.js). Keep this registration right
        // after app.mount(el) — moving it, awaiting between them, or switching to
        // async page components would change the timing and reintroduce an
        // off-by-one in pageview tracking.
        router.on('navigate', (event) => {
            const assetVersion = event.detail.page.props.asset_version;
            const currentAssetVersion = getItem('asset_version') || null;

            if (!currentAssetVersion) {
                setItem('asset_version', assetVersion);
            }

            if (
                currentAssetVersion &&
                assetVersion !== currentAssetVersion &&
                assetVersion !== null
            ) {
                setItem('asset_version', assetVersion);
                window.location.href = event.detail.page.url;
            }

            // Track page view with internal analytics
            try {
                const analytics = getAnalytics();
                analytics.trackPageView(document.title, {
                    page_url: event.detail.page.url,
                    page_component: event.detail.page.component,
                });
            } catch (e) {
                // Silently fail if analytics not initialized
            }

            // woopra-trk.js only fires 'pv' on hard loads; mirror it for SPA
            // navigations. Intentionally not under the production gate below:
            // woopra-trk.js loads in production, staging, and local (see
            // partials/header-scripts.blade.php) and fires its hard-load 'pv'
            // unconditionally, unlike GTM/Meta/HubSpot which are production-only.
            // The tracker no-ops when window.woopra is absent.
            //
            // Defer to the next frame so Vue has re-rendered the <Head>
            // component and updated document.title; otherwise the tracker reads
            // the previous page's title (the navigate event fires before the
            // re-render). This mirrors the hard-load woopra-trk.js payload,
            // which reads document.title after a full render.
            requestAnimationFrame(() => {
                trackWoopraSpaPageview(event.detail.page.url);
            });

            if (appEnv === 'production') {
                if (typeof dataLayer !== 'undefined' && dataLayer) {
                    dataLayer.push({
                        url: event.detail.page.url,
                        event: 'pageview',
                    });
                }

                if (window.fbq) {
                    window.fbq('track', 'PageView');
                }

                if (window._hsq) {
                    window._hsq.push(['setPath', event.detail.page.url]);
                    window._hsq.push(['trackPageView']);
                }
            }
        });

        // Initialize theme store only for authenticated users on app pages.
        // Public auth pages (login, register, password reset) and pre-onboarding
        // pages (quiz, email verification, 2fa) must always render light regardless
        // of any prior localStorage state. Runs synchronously in the same tick as
        // app.mount(el), but after the `navigate` registration above per its NOTE.
        const userData = props.initialPage?.props?.userData;
        const path = window.location.pathname;
        const isPreAppPage =
            /^\/(login|register|forgot-password|reset-password|quiz|email\/verification-notification|verify-block|intermediate|2fa)(\/|$)/.test(
                path
            );
        if (userData && !isPreAppPage) {
            const themeStore = useThemeStore(pinia);
            themeStore.initialize(userData.theme_preference || null);
        } else {
            document.documentElement.classList.remove('dark');
        }

        // Ensure scroll-to-top on each successful navigation between pages
        router.on('success', (event) => {
            try {
                const url = event?.detail?.page?.url || '';
                // Respect anchor navigation
                if (url.includes('#')) return;

                // Defer to next frame to ensure DOM is ready
                requestAnimationFrame(() => {
                    const fBody = document.getElementById('f-body');
                    if (fBody && typeof fBody.scrollTo === 'function') {
                        fBody.scrollTo({ top: 0, behavior: 'auto' });
                    } else {
                        // Fallback to window for layouts without #f-body
                        if (
                            typeof window !== 'undefined' &&
                            typeof window.scrollTo === 'function'
                        ) {
                            window.scrollTo({
                                top: 0,
                                left: 0,
                                behavior: 'auto',
                            });
                        }
                    }
                });
            } catch (_) {}
        });

        // Add error monitoring for Inertia.js response issues
        router.on('error', (event) => {
            if (
                event.detail &&
                event.detail.message &&
                event.detail.message.includes('this.response.headers')
            ) {
                console.error('Inertia.js response headers error detected:', {
                    error: event.detail,
                    url: window.location.href,
                    timestamp: new Date().toISOString(),
                });

                // Send to Sentry with additional context
                if (window.Sentry) {
                    window.Sentry.captureException(
                        new Error('Inertia response headers undefined'),
                        {
                            tags: {
                                component: 'inertia-response-handler',
                            },
                            extra: {
                                originalError: event.detail,
                                url: window.location.href,
                                userAgent: navigator.userAgent,
                            },
                        }
                    );
                }
            }
        });
    },
});
